import { toE164 } from './phone';
import { entriesOf } from './contactMethods';

// BL-177 PR D4: vCard (.vcf, versions 2.1 / 3.0 / 4.0) in and out for the signed-in person's OWN My Contacts. Import produces the same
// row shape as the CSV import (src/lib/contactCsv.js buildImportRows) so one database function (personal_contacts_import_run) handles both.

const unescapeValue = (value) => String(value || '').replace(/\\([nN,;\\])/g, (_, ch) => (ch === 'n' || ch === 'N' ? '\n' : ch));
const escapeValue = (value) => String(value ?? '').replace(/\\/g, '\\\\').replace(/\r?\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');

/** Lines folded by a CRLF + space/tab are joined back (RFC 6350 3.2). Quoted-printable soft breaks (=\n) too. */
function unfold(text) {
    return String(text || '').replace(/^﻿/, '').replace(/\r\n|\r/g, '\n').replace(/\n[ \t]/g, '').replace(/=\n/g, '');
}

function typeToLabel(types, fallback = 'other') {
    const t = types.join(',').toLowerCase();
    if (/(cell|mobile|iphone)/.test(t)) return 'mobile';
    if (/(work|office)/.test(t)) return 'work';
    if (/home/.test(t)) return 'home';
    return fallback;
}

function parseProperty(line) {
    const colon = line.indexOf(':');
    if (colon < 0) return null;
    const left = line.slice(0, colon);
    const value = line.slice(colon + 1);
    const parts = left.split(';');
    const name = parts[0].split('.').pop().toUpperCase();   // "item1.TEL" -> TEL
    const types = [];
    parts.slice(1).forEach((param) => {
        const [key, val] = param.split('=');
        if (val === undefined) types.push(key);                       // vCard 2.1: ;CELL
        else if (/^type$/i.test(key)) types.push(...val.replace(/"/g, '').split(','));
    });
    return { name, types, value };
}

/** vCard text -> import rows ({ row, name, account_name, title, phones, emails, addresses, tags, notes }). */
export function parseVcard(text, defaultCountry = 'US') {
    const cards = unfold(text).split(/BEGIN:VCARD/i).slice(1).map(chunk => chunk.split(/END:VCARD/i)[0]);
    return cards.map((card, index) => {
        const row = { row: index + 1, name: '', account_name: '', title: '', phones: [], emails: [], addresses: [], tags: [], notes: '' };
        let structuredName = '';
        card.split('\n').map(line => line.trim()).filter(Boolean).forEach((line) => {
            const prop = parseProperty(line);
            if (!prop) return;
            const value = prop.value;
            switch (prop.name) {
            case 'FN': row.name = unescapeValue(value).trim(); break;
            case 'N': {
                const [family = '', given = '', , prefix = '', suffix = ''] = value.split(';').map(unescapeValue);
                structuredName = [prefix, given, family, suffix].map(v => v.trim()).filter(Boolean).join(' ');
                break;
            }
            case 'ORG': row.account_name = unescapeValue(value.split(';')[0]).trim(); break;
            case 'TITLE': row.title = unescapeValue(value).trim(); break;
            case 'NOTE': row.notes = unescapeValue(value).trim(); break;
            case 'CATEGORIES': row.tags = value.split(',').map(t => unescapeValue(t).trim()).filter(Boolean); break;
            case 'TEL': {
                const raw = unescapeValue(value).replace(/^tel:/i, '').trim();
                if (raw) row.phones.push({ label: typeToLabel(prop.types, 'mobile'), value: toE164(raw, defaultCountry) || raw });
                break;
            }
            case 'EMAIL': {
                const raw = unescapeValue(value).trim();
                if (raw) row.emails.push({ label: typeToLabel(prop.types, 'work'), value: raw });
                break;
            }
            case 'ADR': {
                const textValue = value.split(';').map(unescapeValue).map(v => v.trim()).filter(Boolean).join(', ');
                if (textValue) row.addresses.push({ label: typeToLabel(prop.types, 'home'), text: textValue });
                break;
            }
            default: break;
            }
        });
        if (!row.name) row.name = structuredName;
        return row;
    }).filter(row => row.name || row.phones.length || row.emails.length);
}

const foldLine = (line) => {
    if (line.length <= 74) return line;
    const chunks = [line.slice(0, 74)];
    for (let i = 74; i < line.length; i += 73) chunks.push(` ${line.slice(i, i + 73)}`);
    return chunks.join('\r\n');
};

const VCARD_TYPE = { mobile: 'CELL', work: 'WORK', home: 'HOME', other: 'OTHER' };

/** The signed-in person's own contacts -> one .vcf file (vCard 3.0). Primary entries carry PREF. */
export function buildVcard(contacts) {
    const lines = [];
    (contacts || []).forEach((contact) => {
        lines.push('BEGIN:VCARD', 'VERSION:3.0');
        const name = String(contact.name || '').trim();
        const words = name.split(/\s+/).filter(Boolean);
        const family = words.length > 1 ? words[words.length - 1] : '';
        const given = words.length > 1 ? words.slice(0, -1).join(' ') : name;
        lines.push(`N:${escapeValue(family)};${escapeValue(given)};;;`, `FN:${escapeValue(name)}`);
        if (contact.company_name) lines.push(`ORG:${escapeValue(contact.company_name)}`);
        if (contact.position) lines.push(`TITLE:${escapeValue(contact.position)}`);
        entriesOf(contact, 'phone').filter(e => (e.value || '').trim()).forEach(e => lines.push(
            `TEL;TYPE=${VCARD_TYPE[e.label] || 'OTHER'}${e.is_primary ? ',PREF' : ''}:${e.value}`));
        entriesOf(contact, 'email').filter(e => (e.value || '').trim()).forEach(e => lines.push(
            `EMAIL;TYPE=${VCARD_TYPE[e.label] === 'CELL' ? 'OTHER' : (VCARD_TYPE[e.label] || 'OTHER')}${e.is_primary ? ',PREF' : ''}:${e.value}`));
        entriesOf(contact, 'address').filter(e => (e.value || '').trim()).forEach(e => lines.push(
            `ADR;TYPE=${VCARD_TYPE[e.label] === 'CELL' ? 'OTHER' : (VCARD_TYPE[e.label] || 'OTHER')}${e.is_primary ? ',PREF' : ''}:;;${escapeValue(e.value)};;;;`));
        if ((contact.tags || []).length) lines.push(`CATEGORIES:${contact.tags.map(escapeValue).join(',')}`);
        if (contact.notes) lines.push(`NOTE:${escapeValue(contact.notes)}`);
        lines.push('END:VCARD');
    });
    return `${lines.map(foldLine).join('\r\n')}\r\n`;
}

export function downloadVcard(filename, text) {
    const blob = new Blob([text], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
